import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Box,
    Paper,
    Typography,
    Chip,
    Divider,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Helper } from '../../../tools/Helper';
import { api_Routes } from '../../../tools/api_Routes';

const EmailTemplateBuilder = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedIds = [], selectAllMode = false, filters = {}, template } = location.state || {};

    const [saveTemplate, setSaveTemplate] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateData, setTemplateData] = useState({
        subject: '',
        body: '',
        isHtml: true
    });
    const [openConfirm, setOpenConfirm] = useState(false);

    useEffect(() => {
        if (template) {
            setTemplateData({
                subject: template.subject,
                body: template.body,
                isHtml: template.is_html
            });
            if (template.id) {
                setSaveTemplate(true);
                setTemplateName(template.name);
            }
        }
    }, [template]);

    const templateVariables = [
        { key: '{{user_name}}', description: 'User full name' },
        { key: '{{user_email}}', description: 'User email address' },
        { key: '{{user_id}}', description: 'User ID' },
        { key: '{{current_date}}', description: 'Current date (YYYY-MM-DD)' }
    ];

    const modules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean']
        ],
    };

    const handleVariableInsert = (variable) => {
        const editor = document.querySelector('.ql-editor');
        if (editor) {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.insertNode(document.createTextNode(variable));
                editor.focus();
            }
        }
    };

    const handleSend = async () => {
        try {
            const payload = {
                recipient_ids: selectAllMode ? 'ALL' : selectedIds,
                filters: selectAllMode ? filters : null,
                subject: templateData.subject,
                body: templateData.body,
                isHtml: templateData.isHtml,
                save_as_template: saveTemplate,
                template_name: templateName
            };

            if (template?.id) {
                payload.template_id = template.id;
            }

            await Helper.Post({
                url: api_Routes.Users.SendBulkEmail,
                data: payload,
                hasToken: true
            });

            navigate('/users', { state: { message: 'Emails queued for sending successfully!' } });
        } catch (error) {
            console.error('Failed to send emails:', error);
        }
    };

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <Typography variant="h4" className="mb-6">
                {template?.id ? 'Edit Email Template' : 'Email Template Builder'}
            </Typography>

            <Paper elevation={3} className="p-6">
                <Box component="form" className="space-y-6">
                    <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={saveTemplate}
                                    onChange={(e) => setSaveTemplate(e.target.checked)}
                                />
                            }
                            label="Save as template"
                        />

                        {saveTemplate && (
                            <TextField
                                fullWidth
                                label="Template Name"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                required
                                size="small"
                                sx={{ mt: 1, maxWidth: 400 }}
                            />
                        )}
                    </Box>

                    <TextField
                        fullWidth
                        label="Email Subject"
                        value={templateData.subject}
                        onChange={(e) => setTemplateData({...templateData, subject: e.target.value})}
                        variant="outlined"
                    />

                    <div className="mb-4">
                        <Typography variant="subtitle1" className="mb-2">Available Variables:</Typography>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {templateVariables.map((varItem) => (
                                <Chip
                                    key={varItem.key}
                                    label={`${varItem.key} - ${varItem.description}`}
                                    onClick={() => handleVariableInsert(varItem.key)}
                                    variant="outlined"
                                    clickable
                                />
                            ))}
                        </div>
                        <Typography variant="body2" color="textSecondary">
                            Click on a variable to insert it into the template
                        </Typography>
                    </div>

                    <Divider className="my-4" />

                    <Typography variant="subtitle1" className="mb-2">Email Body:</Typography>
                    <ReactQuill
                        theme="snow"
                        value={templateData.body}
                        onChange={(value) => setTemplateData({...templateData, body: value})}
                        modules={modules}
                        className="border rounded"
                        style={{ height: '400px', marginBottom: '40px' }}
                    />

                    <div className="flex justify-end space-x-4 pt-4">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate(-1)}
                            size="large"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setOpenConfirm(true)}
                            size="large"
                        >
                            {template?.id ? 'Update and Send' : 'Review and Send'}
                        </Button>
                    </div>
                </Box>
            </Paper>

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} maxWidth="md" fullWidth>
                <DialogTitle>Confirm Email Sending</DialogTitle>
                <DialogContent>
                    <DialogContentText className="mb-4">
                        You are about to send this email to <strong>
                        {selectAllMode ? 'all filtered users' : `${selectedIds.length} selected users`}
                    </strong>. Please review the content:
                    </DialogContentText>

                    <Paper elevation={0} className="p-4 border rounded">
                        <Typography variant="subtitle1" className="font-bold">Subject:</Typography>
                        <Typography className="mb-4">{templateData.subject}</Typography>

                        <Typography variant="subtitle1" className="font-bold">Body Preview:</Typography>
                        <div
                            className="ql-editor"
                            dangerouslySetInnerHTML={{ __html: templateData.body }}
                            style={{ border: 'none' }}
                        />
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleSend} color="primary" variant="contained">
                        Confirm Send
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default EmailTemplateBuilder;